import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FloatingLegendCardProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  contentClassName?: string;
  testId?: string;
  toggleTestId?: string;
  /**
   * When true, the card omits its own bottom-right absolute positioning so a
   * parent stack container can lay several legends out vertically. The card
   * keeps pointer events enabled so it stays interactive inside a
   * pointer-events-none stack wrapper.
   */
  stacked?: boolean;
  /** Initial disclosure state for legends that should stay out of the way. */
  defaultCollapsed?: boolean;
}

export const FloatingLegendCard: React.FC<FloatingLegendCardProps> = ({
  title,
  icon,
  children,
  className,
  bodyClassName,
  contentClassName,
  testId,
  toggleTestId,
  stacked = false,
  defaultCollapsed = false,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div
      data-testid={testId}
      className={cn(
        stacked ? 'pointer-events-auto' : 'absolute bottom-20 right-4 z-30',
        'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-lg',
        'border border-gray-200 dark:border-gray-700',
        'min-w-[180px] max-w-[260px]',
        'transition-all duration-200',
        className,
      )}
    >
      <button
        type="button"
        data-testid={toggleTestId}
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {icon}
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
            {title}
          </span>
        </div>
        {collapsed ? (
          <ChevronUp className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {!collapsed && (
        <div className={cn('border-t border-gray-100 dark:border-gray-700 px-3 pb-2 pt-1', bodyClassName)}>
          <div className={cn('flex flex-col gap-1.5', contentClassName)}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};
